import React, { useState, useEffect } from "react"
import { IEmpleados } from "../../interfaces/IEmpleados"
import { Action, Column } from "../../interfaces/CamposTablaGenerica"
import GenericTable from "../GenericTable/GenericTable"
import { Container, Row, Col, Button, Modal } from "react-bootstrap"
import AddEmpleadoModal from "./AddEmpleadoModal"
import EditEmpleadoModal from "./EditEmpleadoModal"
import { useDeleteUserAuth0 } from "../Auth0/Hooks/useDeleteUserAuth0"
import SkeletonGrid from "../../components/Skeleton/SkeletonGrid"
import { Box } from "@mui/material"
import { useSpinner } from "../../contexts/SpinnerProvider"
import { useApi } from "../../hooks/useApi"
import { API_ENDPOINTS } from "../../api/endpoints"

const Empleados = () => {
  const [empleados, setEmpleados] = useState<IEmpleados[]>([])
  const [editModalShow, setEditModalShow] = useState(false)
  const [addModalShow, setAddModalShow] = useState(false)
  const API_URL = API_ENDPOINTS.EMPLEADOS
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [selectedToDelete, setSelectedToDelete] = useState<IEmpleados | null>(
    null
  )
  const [selectedEditUsuario, setSelectedEditUsuario] =
    useState<IEmpleados | null>(null)
  const { addLoading, removeLoading } = useSpinner()
  const [skeletonLoading, setSkeletonLoading] = useState(false)

  const api = useApi()
  const deleteUserAuth0 = useDeleteUserAuth0()

  const callApiAndSetEmpleados = async () => {
    setSkeletonLoading(true)
    try {
      const responseData = await api.get<IEmpleados[]>(API_URL, {
        errorMessage: "Error al cargar los empleados",
      })
      setEmpleados(responseData)
    } catch {
      // El toast ya fue mostrado por useApi
    } finally {
      setSkeletonLoading(false)
    }
  }

  // Cargar datos de empleados al montar el componente y cuando cambie la URL de la API
  useEffect(() => {
    callApiAndSetEmpleados()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL])

  //Acciones que se pueden realizar
  const actions: Action = {
    create: true,
    update: true,
    delete: true,
  }

  const columns: Column<IEmpleados>[] = [
    // Definir columnas de la tabla
    { title: "Nombre", field: "nombre" },
    { title: "Apellido", field: "apellido" },
    { title: "Email", field: "email" },
    { title: "Contraseña", field: "clave" },
    {
      title: "Estado",
      field: "estado",
      render: (empleado: IEmpleados) => (
        <span className={`${empleado.estado ? "text-danger" : "text-success"}`}>
          {empleado.estado ? (
            <h2>
              <i className="bi bi-lock-fill"></i>
            </h2>
          ) : (
            <h2>
              <i className="bi bi-unlock-fill "></i>
            </h2>
          )}
        </span>
      ),
    },
  ]

  // Función para mostrar el cuadro de diálogo de confirmación de eliminación
  const showDeleteConfirmation = (item: IEmpleados) => {
    setSelectedToDelete(item)
    setDeleteConfirmation(true)
  }

  // Función para busqueda personalizada por ID y nombre
  const customSearch = (searchText: string): Promise<IEmpleados[]> => {
    return new Promise((resolve) => {
      const normalizedSearchText = normalizeString(searchText)

      const filteredData = empleados.filter((empleado) => {
        const normalizedIdUsuario = normalizeString(empleado.id.toString())
        const normalizedNombre = normalizeString(empleado.nombre.toString())
        const normalizedApellido = normalizeString(empleado.apellido.toString())
        const normalizedEmail = normalizeString(empleado.email.toString())

        return (
          normalizedIdUsuario.includes(normalizedSearchText) ||
          normalizedNombre.includes(normalizedSearchText) ||
          normalizedApellido.includes(normalizedSearchText) ||
          normalizedEmail.includes(normalizedSearchText)
        )
      })

      resolve(filteredData)
    })
  }
  //Normaliza el texto para que no importe las Mayus, Minus o Tildes
  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .normalize("NFD") // Eliminar tildes
      .replace(/[\u0300-\u036f]/g, "")
  }

  const handleEmpleadoAdd = async (empleado: IEmpleados) => {
    addLoading()
    try {
      const newEmpleado = await api.post<IEmpleados>(API_URL, empleado, {
        errorMessage: "Error al agregar el empleado",
      })
      if (newEmpleado) {
        setEmpleados([...empleados, newEmpleado])
      }
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const handleEmpleadoEdit = async (empleado: IEmpleados) => {
    addLoading()
    try {
      const updatedEmpleado = await api.put<IEmpleados>(
        `${API_URL}/${empleado.id}`,
        empleado,
        { errorMessage: "Error al editar el empleado" },
      )
      if (updatedEmpleado) {
        setEmpleados(
          empleados.map((item) =>
            item.id === empleado.id ? updatedEmpleado : item,
          ),
        )
      }
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const handleDeleteConfirmation = async (confirmed: boolean) => {
    addLoading()
    if (confirmed && selectedToDelete) {
      const empleadoId: number = selectedToDelete.id
      try {
        await deleteUserAuth0(selectedToDelete.idAuth0)
        await api.del(`${API_URL}/${empleadoId}`, {
          errorMessage: "Error al eliminar el empleado",
        })
        setEmpleados(empleados.filter((item) => item.id !== empleadoId))
      } catch {
        // toast ya mostrado
      } finally {
        removeLoading()
      }
    } else {
      removeLoading()
    }
    setSelectedToDelete(null)
    setDeleteConfirmation(false)
  }

  // Abrir modal para agregar empleado
  const handleAddModalOpen = () => {
    setAddModalShow(true)
  }

  // Cerrar modal para agregar empleado
  const handleAddModalClose = () => {
    setAddModalShow(false)
  }

  // Abrir modal para editar empleado
  const handleEditModalOpen = (item: IEmpleados) => {
    setSelectedEditUsuario(item)
    setEditModalShow(true)
  }

  // Cerrar modal para editar empleado
  const handleEditModalClose = () => {
    setEditModalShow(false)
  }

  return (
    <div>
      <Container fluid>
        <h1 className="text-center mt-3 mb-4" style={{ fontSize: '2.25rem' }}>Lista de Empleados</h1>
        {skeletonLoading ? (
          <SkeletonGrid />
        ) : (
          <>
            <Row className="mt-3">
              <Col sm={12}>
                <Box>
                  <GenericTable
                    data={empleados}
                    columns={columns}
                    actions={actions}
                    onAdd={handleAddModalOpen}
                    onUpdate={handleEditModalOpen}
                    onDelete={showDeleteConfirmation}
                    customSearch={customSearch}
                    isEmployee
                  />
                </Box>
                {deleteConfirmation && (
                  <Modal
                    show={deleteConfirmation}
                    onHide={() => setDeleteConfirmation(false)}
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Confirmar eliminación</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      ¿Estás seguro de que deseas eliminar este egresado de la
                      lista?
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="secondary"
                        onClick={() => handleDeleteConfirmation(false)}
                      >
                        No
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeleteConfirmation(true)}
                      >
                        Sí
                      </Button>
                    </Modal.Footer>
                  </Modal>
                )}
              </Col>
            </Row>
            <AddEmpleadoModal
              show={addModalShow}
              handleClose={handleAddModalClose}
              handleEmpleadoAdd={handleEmpleadoAdd}
            />

            <EditEmpleadoModal
              show={editModalShow}
              handleClose={handleEditModalClose}
              handleEmpleadoEdit={handleEmpleadoEdit}
              selectedEmpleado={selectedEditUsuario}
            />
          </>
        )}
      </Container>
    </div>
  )
}

export default Empleados
